import React from "react";
//@ts-ignore
import imagedevice from "src/assets/images/render1.png";
import { useTranslation } from "react-i18next";
//@ts-ignore
import bagTripod from "src/assets/images/tripod.jpg";
//@ts-ignore
import tripodImg from "src/assets/images/tripode1.jpg";
import { Package2, ReceiptText } from "lucide-react";

interface Props {
    items: any;
}

const Sidebar = ({ items }: Props) => {
    const { t } = useTranslation();

    const formatPrice = (value: number | string) => {
        const num = Number(value) || 0;
        return `${Number.isInteger(num) ? num.toFixed(0) : num.toFixed(2)} €`;
    };

    const accessoriesTotal =
        items.accessories?.length > 0
            ? items.accessories.reduce(
                  (sum: number, accessory: { totalPrice: number }) =>
                      sum + accessory.totalPrice,
                  0,
              )
            : 0;

    const tripodsTotal =
        items.tripods?.length > 0
            ? items.tripods.reduce(
                  (sum: number, tripod: { totalPrice: number }) =>
                      sum + tripod.totalPrice,
                  0,
              )
            : 0;

    const subtotal =
        Number(items.wizards.totalPrice || 0) +
        Number(accessoriesTotal) +
        Number(tripodsTotal);
    const todayTotal = subtotal + Number(items.subscriptions.price || 0);

    return (
        <aside className="sticky top-24 flex h-[calc(100dvh-7rem)] flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-2xl shadow-black/10">
            <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white p-6">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/15 to-indigo-500/15 text-blue-600 ring-1 ring-blue-100">
                        <Package2 className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">
                            {t("sidebar.orderDetails")}
                        </h3>
                        <p className="text-sm text-slate-500">{t("sidebar.summary")}</p>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
                <div className="space-y-3">
                    {items.subscriptions.type !== null && (
                        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-3 shadow-sm">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center space-x-3">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-blue-100 bg-white">
                                        <svg
                                            className="h-5 w-5 text-blue-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900">
                                            {items.subscriptions.type}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {t("sidebar.recurringCost")}
                                        </p>
                                    </div>
                                </div>
                                <p className="shrink-0 text-sm font-bold text-blue-700">
                                    {formatPrice(items.subscriptions.price)}
                                </p>
                            </div>
                        </div>
                    )}

                    {items.wizards.type && items.wizards.quantity > 0 && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center space-x-3">
                                    <img
                                        src={imagedevice}
                                        className="h-12 w-12 rounded-lg border border-gray-200 bg-white p-1 object-contain"
                                        alt="wizard-device"
                                    />
                                    <div className="min-w-0">
                                        <p className="w-60 truncate text-sm font-semibold text-slate-900">
                                            {items.wizards.type}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {t("sidebar.quantity_abbv")}: {items.wizards.quantity}
                                        </p>
                                    </div>
                                </div>
                                <p className="shrink-0 text-sm font-bold text-slate-950">
                                    {formatPrice(items.wizards.price)}
                                </p>
                            </div>
                        </div>
                    )}

                    {items.tripods.length > 0 &&
                        items.tripods.map(
                            (
                                tripod: {
                                    type: string;
                                    price: string;
                                    quantity: number;
                                },
                                index: number,
                            ) => (
                                <div
                                    key={index}
                                    className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex min-w-0 items-center space-x-3">
                                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                                                <img src={tripodImg} alt="tripod" className="h-full w-full object-cover" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="w-60 truncate text-sm font-semibold text-slate-900">
                                                    {tripod.type}
                                                </p>
                                                <p className="text-xs text-slate-500">
                                                    {t("sidebar.quantity_abbv")}: {tripod.quantity}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="shrink-0 text-sm font-bold text-slate-950">
                                            {formatPrice(tripod.price)}
                                        </p>
                                    </div>
                                </div>
                            ),
                        )}

                    {items.accessories.length > 0 &&
                        items.accessories.map(
                            (
                                accessory: {
                                    type: string;
                                    price: string;
                                    quantity: number;
                                },
                                index: number,
                            ) => (
                                <div
                                    key={index}
                                    className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex min-w-0 items-center space-x-3">
                                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                                                <img
                                                    src={bagTripod}
                                                    alt="bag-tripod"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="w-60 truncate text-sm font-semibold text-slate-900">
                                                    {accessory.type}
                                                </p>
                                                <p className="w-40 truncate text-xs text-slate-500">
                                                    {t("sidebar.quantity_abbv")}: {accessory.quantity}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="shrink-0 text-sm font-bold text-slate-950">
                                            {formatPrice(accessory.price)}
                                        </p>
                                    </div>
                                </div>
                            ),
                        )}
                </div>
            </div>

            <div className="border-t border-slate-200 bg-slate-50/80 p-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center gap-2">
                        <ReceiptText className="h-4 w-4 text-blue-600" />
                        <h4 className="text-sm font-semibold text-slate-700">
                            {t("sidebar.summary")}
                        </h4>
                    </div>

                    <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-600">{t("sidebar.price")}</p>
                        <p className="text-sm font-semibold text-slate-900">
                            {formatPrice(subtotal)}
                        </p>
                    </div>

                    {items.subscriptions.type !== null && (
                        <div className="mt-3 flex items-center justify-between">
                            <p className="text-sm text-slate-600">
                                {t("sidebar.recurringCost")}
                            </p>
                            <p className="text-sm font-semibold text-blue-700">
                                {formatPrice(items.subscriptions.price)}
                            </p>
                        </div>
                    )}

                    <div className="mt-3 flex items-center justify-between">
                        <p className="text-sm text-slate-600">{t("sidebar.shipment")}</p>
                        <p className="text-sm font-semibold text-emerald-600">
                            {t("sidebar.free")}
                        </p>
                    </div>

                    <hr className="my-3 border-slate-200" />

                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-700">
                            {t("sidebar.today")}
                        </p>
                        <p className="text-base font-bold text-slate-950">
                            {formatPrice(todayTotal)}
                        </p>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
